/**
 * Header Component - Refactored
 *
 * Main navigation header with responsive design and dropdown menus.
 * Broken down into smaller, focused components for better maintainability.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import NavigationStylesExport from "./NavigationStyles";
import { NavigationLink } from "./NavigationStyles";

// Import extracted sub-components
import UserAvatar from "./header/UserAvatar";
import Logo from "./header/Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  ProductsDropdown,
  MagazineDropdown,
  BusinessDropdown,
  CompanyDropdown,
  AIServicesDropdown,
} from "./header/DesktopDropdowns";
import { MobileButtons, MobileNavigation } from "./header/MobileNavigation";

export default function Header({
  username = null,
  user_image_url = null,
  avatarInitial = null,
  logoUrl,
}) {
  const { t } = useTranslation("common");

  // Desktop dropdown states
  const [dropdownStates, setDropdownStates] = useState({
    products: false,
    magazine: false,
    business: false,
    aiServices: false,
    company: false,
  });

  // Mobile menu states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdownStates, setMobileDropdownStates] = useState({
    products: false,
    magazine: false,
    business: false,
    aiServices: false,
    company: false,
  });

  // Scroll progress for logo animation
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll effect for logo animation
  useEffect(() => {
    let timeoutId = null;

    const handleScroll = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const scrollPosition = window.scrollY;
        const maxScroll = 400;
        const progress = Math.min(scrollPosition / maxScroll, 1);
        setScrollProgress(progress);
      }, 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Navigation data
  const navigationData = {
    products: [
      "TadReamk Eye",
      "TadReamk Create",
      "TadReamk Patent",
      "TadReamk Surveillance",
      "Trademark Trading",
      "Business Card Manager",
    ],
    magazine: ["IP Law", "TadReamk AI", "TadReamk Design", "TadReamk-AIGC", "TadReamk-ERP"],
    business: ["Overview", "API Documentation", "Contact Sales", "Pricing"],
    aiServices: ["Lace Material"],
    company: ["About Us", "Careers"],
  };

  // Helper functions
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileDropdownStates({
      products: false,
      magazine: false,
      business: false,
      aiServices: false,
      company: false,
    });
  };

  const { HEADER_STYLES } = NavigationStylesExport;

  return (
    <header className={HEADER_STYLES.headerMain}>
      <div className={HEADER_STYLES.headerContainer}>
        <div className={HEADER_STYLES.headerContent}>
          {/* Logo - Left */}
          <div className="flex-shrink-0">
            <Logo scrollProgress={scrollProgress} logoUrl={logoUrl} />
          </div>

          {/* Desktop Navigation - Center */}
          <div className={HEADER_STYLES.desktopNav}>
            <nav className={HEADER_STYLES.desktopNavInner}>
              {/* Home Link */}
              <NavigationLink to="/" variant="desktop-link">
                {t("navigation.home")}
              </NavigationLink>

              {/* Products Dropdown */}
              <ProductsDropdown
                productItems={navigationData.products}
                isOpen={dropdownStates.products}
                setIsOpen={(isOpen) =>
                  setDropdownStates((prev) => ({ ...prev, products: isOpen }))
                }
              />

              {/* Business Dropdown */}
              <BusinessDropdown
                businessItems={navigationData.business}
                isOpen={dropdownStates.business}
                setIsOpen={(isOpen) =>
                  setDropdownStates((prev) => ({ ...prev, business: isOpen }))
                }
              />

              {/* Magazine Dropdown */}
              <MagazineDropdown
                magazineItems={navigationData.magazine}
                isOpen={dropdownStates.magazine}
                setIsOpen={(isOpen) =>
                  setDropdownStates((prev) => ({ ...prev, magazine: isOpen }))
                }
              />

              {/* AI Services Dropdown */}
              <AIServicesDropdown
                aiServicesItems={navigationData.aiServices}
                isOpen={dropdownStates.aiServices}
                setIsOpen={(isOpen) =>
                  setDropdownStates((prev) => ({ ...prev, aiServices: isOpen }))
                }
              />

              {/* Company Dropdown */}
              <CompanyDropdown
                companyItems={navigationData.company}
                isOpen={dropdownStates.company}
                setIsOpen={(isOpen) =>
                  setDropdownStates((prev) => ({ ...prev, company: isOpen }))
                }
              />

              {/* News Link */}
              <NavigationLink to="/news" replace={true} variant="desktop-link">
                {t("navigation.news")}
              </NavigationLink>
            </nav>
          </div>

          {/* Desktop Right - Language Switcher & User Authentication */}
          <div className={HEADER_STYLES.desktopRight}>
            <LanguageSwitcher variant="desktop" />
            {username ? (
              <UserAvatar
                username={username}
                userImageUrl={user_image_url}
                avatarInitial={avatarInitial}
              />
            ) : (
              <NavigationLink to="/signin" variant="desktop-auth">
                {t("navigation.signIn")}
              </NavigationLink>
            )}
          </div>

          {/* Mobile Buttons - Right */}
          <MobileButtons
            isMobileMenuOpen={isMobileMenuOpen}
            toggleMobileMenu={toggleMobileMenu}
            username={username}
            userImageUrl={user_image_url}
            avatarInitial={avatarInitial}
          />
        </div>

        {/* Mobile Navigation Menu */}
        <MobileNavigation
          isMobileMenuOpen={isMobileMenuOpen}
          productItems={navigationData.products}
          magazineItems={navigationData.magazine}
          businessItems={navigationData.business}
          aiServicesItems={navigationData.aiServices}
          companyItems={navigationData.company}
          dropdownStates={mobileDropdownStates}
          setDropdownStates={setMobileDropdownStates}
          closeMobileMenu={closeMobileMenu}
        />
      </div>
    </header>
  );
}
